import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import axiosInstance from './axiosInstance';
import _ from 'lodash';
import { 
  User, 
  Mail, 
  Lock, 
  Mic, 
  Headphones,
  Eye,
  EyeOff,
  AlertCircle,
  Check,  // Added
  X       // Added
} from 'lucide-react';

const LoginRegister = () => {
  // Form states
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [isUsernameAvailable, setIsUsernameAvailable] = useState(null);
  
  // UI states
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formValid, setFormValid] = useState(false);

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (isLogin) {
      setFormValid(email && password);
    } else {
      setFormValid(username && email && password && role);
    }
  }, [username, email, password, role, isLogin]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const checkUsername = _.debounce(async (username) => {
    if (!username || username.length < 3) {
      setIsUsernameAvailable(null);
      return;
    }
  
    setIsCheckingUsername(true);
    try {
      const response = await fetch(
        `https://api.talkconnect.live/api/auth/check-username/${username}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
  
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
  
      const data = await response.json();
      setIsUsernameAvailable(data.isAvailable);
    } catch (err) {
      console.error('Error checking username:', err);
      setError('Error checking username availability');
    } finally {
      setIsCheckingUsername(false);
    }
  }, 500);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !formValid) return;

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    if (!isLogin && !role) {
      setError("Please select a role (Talker or Listener)");
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const data = isLogin 
        ? { email, password } 
        : { username, email, password, role };

      const response = await axiosInstance.post(endpoint, data);

      if (response.data.token) {
        // Store token first
        localStorage.setItem('authToken', response.data.token);
        
        // Update axios default headers
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;

        // Prepare user data
        const userData = {
          ...response.data.user,
          deviceInfo: {
            type: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
            browser: navigator.userAgent,
            platform: navigator.platform,
            timestamp: Date.now()
          }
        };

        // Now handle login
        const loginSuccess = await login(response.data.token, userData);
        
        if (loginSuccess) {
          navigate('/', { replace: true });
        } else {
          throw new Error('Login process failed');
        }
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error("Auth error:", err);
      
      if (!err.response) {
        setError('Network error. Please check your internet connection.');
        return;
      }

      const errorCode = err.response?.data?.code;
      switch(errorCode) {
        case 'EMAIL_EXISTS':
          setError('This email is already registered.');
          break;
        case 'INVALID_CREDENTIALS':
          setError('Invalid email or password.');
          break;
        case 'MISSING_FIELDS':
          setError('Please fill in all required fields.');
          break;
        case 'REGISTRATION_ERROR':
          setError('Registration failed. Please try again.');
          break;
        case 'LOGIN_ERROR':
          setError('Login failed. Please try again.');
          break;
        default:
          setError(err.response?.data?.message || 'Authentication failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRoleSelect = (selectedRole) => {
    setRole(selectedRole);
    setError('');
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setUsername('');
    setEmail('');
    setPassword('');
    setRole('');
  };

  useEffect(() => {
    return () => checkUsername.cancel();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-sm">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900">
            {isLogin ? 'Welcome' : 'Create Account'}
          </h2>
          <p className="mt-2 text-sm text-gray-600 mb-4">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button
              onClick={toggleMode}
              className="font-medium text-teal-500 hover:text-teal-600"
              disabled={isSubmitting}
            >
              {isLogin ? 'Register here' : 'Login here'}
            </button>
          </p>
  
          {/* Feature Pills - New Addition */}
          <div className="flex flex-wrap justify-center gap-2 mb-2">
            <span className="bg-teal-50 text-teal-600 px-3 py-1 rounded-full text-sm font-medium">
              Safe Space
            </span>
            <span className="bg-teal-50 text-teal-600 px-3 py-1 rounded-full text-sm font-medium">
              Anonymous
            </span>
            <span className="bg-teal-50 text-teal-600 px-3 py-1 rounded-full text-sm font-medium">
              Real Conversations
            </span>
          </div>
        </div>
  
        {/* Rest of the code remains the same */}
        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* Username field (Register only) */}
          {!isLogin && (
  <div>
    <label className="block text-sm font-medium text-gray-700">
      Username
    </label>
    <div className="mt-1 relative rounded-lg shadow-sm">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <User className="h-5 w-5 text-gray-400" />
      </div>
      <input
        type="text"
        value={username}
        onChange={(e) => {
          setUsername(e.target.value);
          checkUsername(e.target.value);
        }}
        className="block w-full pl-10 pr-12 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
        placeholder="Enter your username"
        disabled={isSubmitting}
        required
      />
      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
        {isCheckingUsername ? (
          <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : isUsernameAvailable === true ? (
          <Check className="h-5 w-5 text-green-500" />
        ) : isUsernameAvailable === false ? (
          <X className="h-5 w-5 text-rose-500" />
        ) : null}
      </div>
    </div>
    {isUsernameAvailable === false && (
      <p className="mt-1 text-sm text-rose-600">This username is already taken</p>
    )}
  </div>
)}
  
          {/* Email field */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <div className="mt-1 relative rounded-lg shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                placeholder="Enter your email"
                disabled={isSubmitting}
                required
              />
            </div>
          </div>
  
          {/* Password field */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <div className="mt-1 relative rounded-lg shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full pl-10 pr-12 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                placeholder="Enter your password"
                disabled={isSubmitting}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showPassword ? 
                  <EyeOff className="h-5 w-5" /> : 
                  <Eye className="h-5 w-5" />
                }
              </button>
            </div>
          </div>
  
          {/* Role selection (Register only) */}
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Role
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div
                  onClick={() => !isSubmitting && handleRoleSelect('talker')}
                  className={`cursor-pointer p-4 rounded-lg border-2 transition-colors text-center
                    ${role === 'talker' 
                      ? 'border-teal-500 bg-teal-50 text-teal-700' 
                      : 'border-gray-200 hover:bg-gray-50'
                    } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Mic className="mx-auto h-6 w-6 mb-2" />
                  <span className="font-medium">Talker</span>
                </div>
                <div
                  onClick={() => !isSubmitting && handleRoleSelect('listener')}
                  className={`cursor-pointer p-4 rounded-lg border-2 transition-colors text-center
                    ${role === 'listener' 
                      ? 'border-teal-500 bg-teal-50 text-teal-700' 
                      : 'border-gray-200 hover:bg-gray-50'
                    } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Headphones className="mx-auto h-6 w-6 mb-2" />
                  <span className="font-medium">Listener</span>
                </div>
              </div>
            </div>
          )}
  
          {/* Error message */}
          {error && (
            <div className="rounded-lg bg-rose-50 p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-rose-400" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-rose-800">{error}</p>
                </div>
              </div>
            </div>
          )}
  
          {/* Submit button */}
          <div>
            <button
              type="submit"
              disabled={isSubmitting || !formValid}
              className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-teal-500 hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500
                ${(isSubmitting || !formValid) ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isSubmitting ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </button>
          </div>
        </form>
  
        {/* Password requirements (Register only) */}
        {!isLogin && (
          <div className="mt-4 space-y-1">
            <p className="text-sm text-gray-600 font-medium">Password must:</p>
            <div className="space-y-1 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-400"></div>
                <span>Be at least 6 characters long</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-400"></div>
                <span>Contain at least one number</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-400"></div>
                <span>Contain at least one letter</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginRegister;